import { useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { Building, MapPin, Phone, Mail, Globe, Clock, Image } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { apiRequest } from '@/lib/queryClient';
import { businessCategories } from '@shared/schema';

export default function AddBusinessPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    hours: '',
    latitude: 33.7490, // Default to Atlanta
    longitude: -84.3880,
    // Enhanced fields for FBA and hustle culture
    businessType: 'established',
    ownerAge: '',
    foundationalBlackAmerican: true,
    certifications: [] as string[],
    socialMedia: {
      instagram: '',
      facebook: '',
      twitter: '',
      tiktok: '',
      linkedin: '',
    },
    servicesOffered: [] as string[],
    priceRange: '',
    mobileService: false,
    acceptsCards: false,
    acceptsCash: true,
    acceptsVenmo: false,
    acceptsCashApp: false,
    tags: [] as string[],
    isHiring: false,
    openPositions: [] as string[],
  });

  const createBusinessMutation = useMutation({
    mutationFn: (businessData: typeof formData) => 
      apiRequest('/api/businesses', {
        method: 'POST',
        body: JSON.stringify({
          ...businessData,
          ownerId: user?.uid || 'anonymous'
        })
      }),
    onSuccess: () => {
      setLocation('/');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      alert('Please sign in to add a business');
      return;
    }
    createBusinessMutation.mutate(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSocialMediaChange = (platform: string, value: string) => {
    setFormData({
      ...formData,
      socialMedia: {
        ...formData.socialMedia,
        [platform]: value
      }
    });
  };

  const handleArrayChange = (fieldName: string, values: string[]) => {
    setFormData({
      ...formData,
      [fieldName]: values
    });
  };

  const addToArray = (fieldName: string, value: string) => {
    const currentArray = formData[fieldName as keyof typeof formData] as string[];
    if (value && !currentArray.includes(value)) {
      handleArrayChange(fieldName, [...currentArray, value]);
    }
  };

  const removeFromArray = (fieldName: string, index: number) => {
    const currentArray = formData[fieldName as keyof typeof formData] as string[];
    handleArrayChange(fieldName, currentArray.filter((_, i) => i !== index));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Sign In Required</h1>
          <p className="text-gray-600 mb-6">You need to be signed in to add a business.</p>
          <button
            onClick={() => setLocation('/login')}
            className="px-6 py-2 bg-red-700 text-white rounded-md hover:bg-red-800"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-8">
              <Building size={48} className="mx-auto text-red-700 mb-4" />
              <h1 className="text-3xl font-bold text-gray-900">List Your Business</h1>
              <p className="text-gray-600 mt-2">
                Join our community of Black-owned businesses and connect with customers who value supporting our community.
              </p>
            </div>

            {/* Legal Disclaimer */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-800 mb-3">Important Legal Disclaimer</h3>
              <p className="text-sm text-red-700 mb-3">
                By submitting your business information, you acknowledge and agree that:
              </p>
              <ul className="text-sm text-red-700 space-y-1 mb-3">
                <li>• Your business operates in full compliance with all applicable local, state, and federal laws</li>
                <li>• You will not use this platform to promote or facilitate any illegal activities</li>
                <li>• All information provided is accurate and truthful</li>
                <li>• The Black Book reserves the right to remove any listing that violates these terms</li>
              </ul>
              <p className="text-sm text-red-700 font-medium">
                Violation of these terms may result in permanent removal from the platform and potential legal action.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                      placeholder="Your business name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="">Select a category</option>
                      {businessCategories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Type *
                    </label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="established">Established Business</option>
                      <option value="startup">Startup</option>
                      <option value="side_hustle">Side Hustle</option>
                      <option value="youth_entrepreneur">Youth Entrepreneur</option>
                    </select>
                  </div>

                  {formData.businessType === 'youth_entrepreneur' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Owner Age
                      </label>
                      <input
                        type="number"
                        name="ownerAge"
                        value={formData.ownerAge}
                        onChange={handleChange}
                        min="10"
                        max="25"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                        placeholder="Your age"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price Range
                    </label>
                    <select
                      name="priceRange"
                      value={formData.priceRange}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="">Select price range</option>
                      <option value="$">$ (Budget friendly)</option>
                      <option value="$$">$$ (Moderate)</option>
                      <option value="$$$">$$$ (Premium)</option>
                      <option value="$$$$">$$$$ (Luxury)</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    placeholder="Tell us about your business..."
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin size={20} className="mr-2" />
                  Location
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                        placeholder="Atlanta"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        maxLength={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                        placeholder="GA"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                        placeholder="30309"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone size={16} className="inline mr-1" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail size={16} className="inline mr-1" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                      placeholder="info@yourbusiness.com"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe size={16} className="inline mr-1" />
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    placeholder="https://yourbusiness.com"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock size={16} className="inline mr-1" />
                    Business Hours
                  </label>
                  <input
                    type="text"
                    name="hours"
                    value={formData.hours}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    placeholder="Mon-Fri 9am-6pm, Sat 10am-4pm"
                  />
                </div>
              </div>

              {/* Legal Acknowledgment */}
              <div className="flex items-center mb-6">
                <input 
                  type="checkbox" 
                  id="terms" 
                  className="h-4 w-4 text-red-700 focus:ring-red-600 border-gray-300 rounded" 
                  required 
                />
                <label htmlFor="terms" className="ml-2 text-sm text-gray-700">
                  I acknowledge that I have read and agree to the legal disclaimer above and certify that my business operates legally. *
                </label>
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t border-gray-200">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-700">
                    <strong>Note:</strong> All business listings are reviewed before being published. We'll contact you within 48 hours to verify your submission.
                  </p>
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setLocation('/')}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createBusinessMutation.isPending}
                    className="px-6 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 disabled:bg-gray-400"
                  >
                    {createBusinessMutation.isPending ? 'Submitting...' : 'Submit Business Listing'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}